import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Search, Globe } from 'lucide-react';

const Header = ({ language, setLanguage }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    {
      label: language === 'bm' ? 'Laman Utama' : 'Home',
      path: '/'
    },
    {
      label: language === 'bm' ? 'Maklumat Korporat' : 'Corporate Info',
      path: '#',
      dropdown: [
        { label: language === 'bm' ? 'Sejarah JPJ' : 'JPJ History', path: '/sejarah' },
        { label: language === 'bm' ? 'Visi & Misi' : 'Vision & Mission', path: '/visi-misi' },
        { label: language === 'bm' ? 'Carta Organisasi' : 'Organization Chart', path: '/carta' },
        { label: language === 'bm' ? 'Piagam Pelanggan' : 'Customer Charter', path: '/piagam' },
      ]
    },
    {
      label: language === 'bm' ? 'Perkhidmatan' : 'Services',
      path: '#',
      dropdown: [
        { label: language === 'bm' ? 'Semak Lesen Memandu' : 'Check Driving License', path: '/semak-lesen' },
        { label: language === 'bm' ? 'Pembaharuan Lesen' : 'License Renewal', path: '/pembaharuan' },
        { label: language === 'bm' ? 'Semak Saman' : 'Check Summons', path: '/semak-saman' },
        { label: language === 'bm' ? 'Pertukaran Lesen Asing' : 'Foreign License Exchange', path: '/pertukaran' },
      ]
    },
    {
      label: language === 'bm' ? 'Pusat Media' : 'Media Centre',
      path: '#',
      dropdown: [
        { label: language === 'bm' ? 'Kenyataan Media' : 'Media Statements', path: '/media' },
        { label: language === 'bm' ? 'Pengumuman' : 'Announcements', path: '/pengumuman' },
        { label: language === 'bm' ? 'Soalan Lazim' : 'FAQ', path: '/faq' },
      ]
    },
    {
      label: language === 'bm' ? 'Hubungi Kami' : 'Contact Us',
      path: '/hubungi'
    }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="w-full">
      {/* Top Government Bar */}
      <div style={{ backgroundColor: '#00264d' }} className="text-white text-xs py-1.5">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-medium opacity-90">Portal Rasmi JPJ</span>
            <span className="opacity-40">|</span>
            <a href="#" className="opacity-75 hover:opacity-100 transition-opacity">Portal Rakan Strategik</a>
            <span className="opacity-40">|</span>
            <a href="#" className="opacity-75 hover:opacity-100 transition-opacity">MySIKAP</a>
            <span className="opacity-40">|</span>
            <a href="#" className="opacity-75 hover:opacity-100 transition-opacity">JPJeBid</a>
            <span className="opacity-40">|</span>
            <a href="#" className="opacity-75 hover:opacity-100 transition-opacity">MyJPJ</a>
          </div>
          <div className="flex items-center gap-3">
            <Globe size={13} className="opacity-75" />
            <button
              onClick={() => setLanguage('bm')}
              className={`font-medium transition-all ${
                language === 'bm' ? 'text-yellow-300 font-bold' : 'opacity-75 hover:opacity-100'
              }`}
            >
              BM
            </button>
            <span className="opacity-40">|</span>
            <button
              onClick={() => setLanguage('en')}
              className={`font-medium transition-all ${
                language === 'en' ? 'text-yellow-300 font-bold' : 'opacity-75 hover:opacity-100'
              }`}
            >
              EN
            </button>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div style={{ backgroundColor: '#003580' }} className="text-white">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          {/* Logo Section */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
            <img
              src="https://upload.wikimedia.org/wikipedia/commons/thumb/9/9b/Jabatan_Pengangkutan_Jalan.svg/120px-Jabatan_Pengangkutan_Jalan.svg.png"
              alt="Logo JPJ Malaysia"
              style={{ width: 52, height: 54, objectFit: 'contain' }}
            />
            <div>
              <div className="font-bold text-sm leading-tight">JABATAN PENGANGKUTAN JALAN</div>
              <div className="text-xs opacity-80 leading-tight">ROAD TRANSPORT DEPARTMENT</div>
              <div className="text-xs opacity-80 leading-tight">MALAYSIA</div>
            </div>
          </Link>

          {/* Right side - Search + Contact */}
          <div className="hidden md:flex items-center gap-6">
            <div className="text-right text-xs opacity-80">
              <div className="font-semibold">Pusat Panggilan</div>
              <div className="text-yellow-300 font-bold text-sm">+603 8000 8000</div>
            </div>
            <button
              onClick={() => navigate('/semak-lesen')}
              className="flex items-center gap-2 px-4 py-2 rounded text-sm font-semibold transition-all"
              style={{ backgroundColor: '#cc0000' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#aa0000'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#cc0000'}
            >
              <Search size={14} />
              {language === 'bm' ? 'Semak Lesen' : 'Check License'}
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2 rounded"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Navigation Bar */}
      <div style={{ backgroundColor: '#003580', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
        <div className="max-w-7xl mx-auto px-4">
          <nav className="hidden md:flex">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative group"
                onMouseEnter={() => item.dropdown && setActiveDropdown(item.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.path}
                  className={`flex items-center gap-1 px-4 py-3 text-sm font-medium text-white transition-all relative ${
                    isActive(item.path) ? 'bg-white/10' : 'hover:bg-white/10'
                  }`}
                >
                  {item.label}
                  {item.dropdown && <ChevronDown size={12} className="opacity-70" />}
                  {isActive(item.path) && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-yellow-400" />
                  )}
                </Link>

                {/* Dropdown Menu */}
                {item.dropdown && activeDropdown === item.label && (
                  <div
                    className="absolute top-full left-0 z-50 min-w-52 shadow-lg"
                    style={{ backgroundColor: '#002a6e' }}
                    onMouseEnter={() => setActiveDropdown(item.label)}
                    onMouseLeave={() => setActiveDropdown(null)}
                  >
                    {item.dropdown.map((sub) => (
                      <Link
                        key={sub.path}
                        to={sub.path}
                        className="block px-4 py-2.5 text-sm text-white/90 hover:bg-white/10 hover:text-white transition-all border-b border-white/10 last:border-b-0"
                        onClick={() => setActiveDropdown(null)}
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile Navigation */}
          {menuOpen && (
            <nav className="md:hidden pb-4">
              {navItems.map((item) => (
                <div key={item.label}>
                  <Link
                    to={item.path}
                    className="block px-2 py-2.5 text-sm text-white/90 hover:bg-white/10 border-b border-white/10"
                    onClick={() => setMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                  {item.dropdown && item.dropdown.map((sub) => (
                    <Link
                      key={sub.path}
                      to={sub.path}
                      className="block pl-6 py-2 text-xs text-white/70 hover:bg-white/10 border-b border-white/5"
                      onClick={() => setMenuOpen(false)}
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              ))}
            </nav>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
